import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { WizardStepper } from '../wizard/WizardStepper';
import { ShieldCheck, Save } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAssessmentLibrary } from '../../hooks/useAssessmentLibrary';
import { Button } from '../ui/Button';
import { Assessment } from '../../types/assessment';
const EU_STEPS = [{
  id: 'system',
  label: 'System',
  path: '/assessment/system'
}, {
  id: 'regulation',
  label: 'Regulation',
  path: '/assessment/regulation'
}, {
  id: 'risk',
  label: 'Risk Class',
  path: '/assessment/risk'
}, {
  id: 'evidence',
  label: 'Evidence',
  path: '/assessment/evidence'
}, {
  id: 'analysis',
  label: 'Analysis',
  path: '/assessment/analysis'
}];
const NIST_STEPS = [{
  id: 'system',
  label: 'System',
  path: '/assessment/system'
}, {
  id: 'regulation',
  label: 'Regulation',
  path: '/assessment/regulation'
}, {
  id: 'govern',
  label: 'Govern',
  path: '/assessment/nist/govern'
}, {
  id: 'map',
  label: 'Map',
  path: '/assessment/nist/map'
}, {
  id: 'measure',
  label: 'Measure',
  path: '/assessment/nist/measure'
}, {
  id: 'manage',
  label: 'Manage',
  path: '/assessment/nist/manage'
}, {
  id: 'analysis',
  label: 'Analysis',
  path: '/assessment/nist/analysis'
}];
export function AssessmentLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated
  } = useAuth();
  const {
    updateAssessment,
    addAssessment,
    getAssessment
  } = useAssessmentLibrary(user?.email || null);
  const isNist = location.pathname.includes('/assessment/nist');
  const STEPS = isNist ? NIST_STEPS : EU_STEPS;
  // Determine current step index based on path
  const currentStepIndex = STEPS.findIndex(step => location.pathname.includes(step.path));
  // Default to 0 if not found (e.g. root assessment path or audit trail)
  const activeIndex = currentStepIndex === -1 ? 0 : currentStepIndex;
  // Check if we're on audit trail page
  const isAuditTrail = location.pathname.includes('/audit-trail');
  const handleSaveAndReturn = () => {
    if (!isAuthenticated) return;
    // Read directly from localStorage to get the absolute latest data
    // This ensures we capture all updates from any component
    const storedAssessment = localStorage.getItem('provenance_one_assessment');
    if (!storedAssessment) return;
    const currentAssessment: Assessment = JSON.parse(storedAssessment);
    // Update timestamp
    currentAssessment.updatedAt = new Date().toISOString();
    // Check if assessment already exists in library
    const existingAssessment = getAssessment(currentAssessment.id);
    if (existingAssessment) {
      // Update existing assessment
      updateAssessment(currentAssessment.id, currentAssessment);
    } else {
      // Add new assessment to library
      addAssessment(currentAssessment);
    }
    // Navigate to library
    navigate('/library');
  };
  return <div className="min-h-screen bg-navy-900 flex flex-col" data-id="element-142">
      {/* Header */}
      <header className="bg-navy-800 border-b border-slate-800 sticky top-0 z-50" data-id="element-143">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-144">
          <button onClick={() => navigate('/')} className="flex items-center space-x-3 hover:opacity-80 transition-opacity" data-id="element-145">
            <div className="bg-teal-500/10 p-2 rounded-lg" data-id="element-146">
              <ShieldCheck className="h-6 w-6 text-teal-500" data-id="element-147" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight" data-id="element-148">
              ProvenanceOne
            </span>
          </button>

          {!isAuditTrail && <div className="hidden md:block" data-id="element-149">
              <WizardStepper steps={STEPS} currentStepIndex={activeIndex} data-id="element-150" />
            </div>}

          <div className="flex items-center space-x-4" data-id="element-151">
            {isAuthenticated && <>
                <Button variant="secondary" size="sm" onClick={handleSaveAndReturn} className="hidden sm:flex" data-id="element-152">
                  <Save className="h-4 w-4 mr-2" data-id="element-153" />
                  Save & Return to Library
                </Button>
                <button onClick={handleSaveAndReturn} className="sm:hidden text-slate-400 hover:text-teal-400 transition-colors p-2" aria-label="Save and return to library" data-id="element-154">
                  <Save className="h-5 w-5" data-id="element-155" />
                </button>
              </>}
            <button onClick={() => navigate('/assessment/audit-trail')} className="text-slate-400 hover:text-teal-400 transition-colors text-sm font-medium" data-id="element-156">
              Audit Trail
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-id="element-157">
        <Outlet data-id="element-158" />
      </main>
    </div>;
}